import React, { useContext, useState } from "react";
import { Image, Typography, Space } from "antd";
import {
  HistoryOutlined,
  UpOutlined,
  DownOutlined,
} from "@ant-design/icons";
import midnightIcon from "../../../../assets/images/nutritionistIcons/midnight.svg";
import morningIcon from "../../../../assets/images/nutritionistIcons/morning.svg";
import afternoonIcon from "../../../../assets/images/nutritionistIcons/afternoon.svg";
import nightIcon from "../../../../assets/images/nutritionistIcons/night.svg";
import EditIcon from "../../../ui/EditIcon";
import dayjs from "dayjs";
import DietEditChart from "./EditChart/DietEditChart";
import { UserContext } from "../../../../context/UserContext";
import { getCookie } from "../../../../helpers/localStorage";
import { Role } from "../../../../constants/defaultKeys";
import useQuery from "../../../../hooks/useQuery";
import DietHistoryDrawer from "./DietHistory/DietHistory";
import "./DietView.css";

const NUTRIENT_META = {
  energy: { label: "Energy", unit: "kcal", tone: "orange" },
  proteins: { label: "Proteins", unit: "g", tone: "purple" },
  carbs: { label: "Carbs", unit: "g", tone: "green" },
  sodium: { label: "Sodium", unit: "mg", tone: "blue" },
  potassium: { label: "Potassium", unit: "mg", tone: "pink" },
};

const SECTION_CONFIG = [
  {
    key: "mid_night",
    title: "Midnight",
    icon: midnightIcon,
    iconTone: "midnight",
  },
  {
    key: "morning",
    title: "Morning",
    icon: morningIcon,
    iconTone: "morning",
  },
  {
    key: "afternoon",
    title: "Afternoon",
    icon: afternoonIcon,
    iconTone: "afternoon",
  },
  {
    key: "evening",
    title: "Night",
    icon: nightIcon,
    iconTone: "night",
  },
];

const DietView = ({ dietData, refresh, dietDetails }) => {
  const { userDetails } = useContext(UserContext);
  const [editState, setEditState] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  const defaultRole = userDetails?.currRole || getCookie(Role);
  const role = defaultRole.toLowerCase()?.replaceAll("_", "-");
  const isActive = useQuery()?.status === "current";

  const calculateDietTotals = (data) => {
    return data?.reduce(
      (totals, timeSlot) => {
        timeSlot?.items?.forEach((item) => {
          totals.proteins += (item.diet__protien || 0) * item.quantity;
          totals.calories += (item.diet__calories || 0) * item.quantity;
          totals.sodium += (item.diet__sodium || 0) * item.quantity;
          totals.potassium += (item.diet__potassium || 0) * item.quantity;
          totals.carbohydrate +=
            (item.diet__carbohydrates || 0) * item.quantity;
        });
        return totals;
      },
      { calories: 0, proteins: 0, carbohydrate: 0, sodium: 0, potassium: 0 }
    );
  };

  const overallData = [
    ...(dietData?.slots?.mid_night || []),
    ...(dietData?.slots?.morning || []),
    ...(dietData?.slots?.afternoon || []),
    ...(dietData?.slots?.evening || []),
  ];

  const toggleSection = (key) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const formatValue = (value) => {
    if (value === undefined || value === null) return "0";
    const num = Number(value);
    return Number.isInteger(num) ? String(num) : num.toFixed(1);
  };

  const renderNutrientChips = () => {
    const dietTotals = calculateDietTotals([...overallData]);
    const required =
      dietDetails?.nutritional_recommendations ??
      dietDetails?.ia?.nutritional_recommendations;

    const chips = [
      {
        key: "energy",
        current: dietTotals?.calories,
        target: required?.calories,
      },
      {
        key: "proteins",
        current: dietTotals?.proteins,
        target: required?.protein,
      },
      {
        key: "carbs",
        current: dietTotals?.carbohydrate,
        target: required?.carbohydrates,
      },
      {
        key: "sodium",
        current: dietTotals?.sodium,
        target: required?.sodium,
      },
      {
        key: "potassium",
        current: dietTotals?.potassium,
        target: required?.potassium,
      },
    ].filter(
      (chip) => chip.current !== undefined && chip.target !== undefined
    );

    if (!chips.length) return null;

    return (
      <div className="diet-nutrient-chips">
        {chips.map((chip) => {
          const meta = NUTRIENT_META[chip.key];
          return (
            <div
              key={chip.key}
              className={`diet-nutrient-chip diet-nutrient-chip--${meta.tone}`}
            >
              <span className="diet-nutrient-chip__dot" />
              <span className="diet-nutrient-chip__label">{meta.label}</span>
              <span className="diet-nutrient-chip__value">
                <span className="diet-nutrient-chip__current">
                  {formatValue(chip.current)}
                </span>
                <span className="diet-nutrient-chip__sep">/</span>
                <span className="diet-nutrient-chip__target">
                  {formatValue(chip.target)}
                </span>
              </span>
              <span className="diet-nutrient-chip__unit">{meta.unit}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSlotItems = (day) => {
    const items = day?.items || [];
    if (!items.length) return null;

    return (
      <div className="diet-slot-cell">
        {items.map((item, index) => {
          const comment =
            item?.comments?.trim() ||
            (index === 0 ? day?.comments?.trim() : "") ||
            "";

          return (
            <div
              key={`${day?.time}-${index}`}
              className="diet-slot-item"
            >
              <span className="diet-slot-item__name">
                {item?.diet__name} x {item?.quantity}
                {comment ? (
                  <>
                    {", "}
                    <span className="diet-slot-item__comment">{comment}</span>
                  </>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSection = ({ key, title, icon, iconTone }, mealData) => {
    if (!mealData?.length) return null;

    const isCollapsed = !!collapsedSections[key];
    const slotCount = mealData.length;

    return (
      <div className="diet-period-card" key={key}>
        <div className="diet-period-card__header">
          <div className="diet-period-card__title-wrap">
            <div
              className={`diet-period-card__icon diet-period-card__icon--${iconTone}`}
            >
              <Image src={icon} preview={false} alt={title} />
            </div>
            <Typography.Text className="diet-period-card__title">
              {title}
            </Typography.Text>
            <span
              className={`diet-period-card__slot-badge diet-period-card__slot-badge--${iconTone}`}
            >
              {slotCount} time slot{slotCount === 1 ? "" : "s"}
            </span>
          </div>
          <button
            type="button"
            className="diet-period-card__collapse"
            onClick={() => toggleSection(key)}
          >
            {isCollapsed ? "Expand" : "Collapse"}
            {isCollapsed ? <DownOutlined /> : <UpOutlined />}
          </button>
        </div>

        {!isCollapsed && (
          <div className="diet-period-card__body">
            <div
              className="diet-timeline-grid"
              style={{ "--diet-slot-count": slotCount }}
            >
              {mealData.map((day) => (
                <div className="diet-timeline-col" key={day?.time}>
                  <div className="diet-timeline-col__time">
                    {dayjs(String(day?.time), "HH").format("hh A")}
                  </div>
                  <div className="diet-timeline-col__items">
                    {renderSlotItems(day)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="diet-view-root">
      {isActive && (
        <>
          {editState ? (
            <DietEditChart
              dietEditData={dietData}
              refresh={refresh}
              setEditState={setEditState}
              dietDetails={dietDetails}
            />
          ) : (
            <div className="diet-view-layout">
              {role === "nutritionist" &&
                (dietDetails?.dietType || dietDetails?.ia?.dietType) && (
                  <div className="diet-view-toolbar">
                    <Typography.Text className="diet-view-type">
                      Diet Type -
                      <span className="diet-view-type__value">
                        {dietDetails?.dietType || dietDetails?.ia?.dietType}
                      </span>
                    </Typography.Text>

                    <Space size="large">
                      <HistoryOutlined
                        onClick={() => setHistoryVisible(true)}
                        className="diet-view-action-icon"
                      />
                      {isActive && (
                        <EditIcon
                          onClick={() => setEditState(true)}
                          size={20}
                          className="diet-view-action-icon"
                        />
                      )}
                    </Space>
                  </div>
                )}

              {renderNutrientChips()}

              {dietData?.comments && (
                <div className="diet-view-comments">
                  <span className="diet-view-comments__label">Comments:</span>
                  <span className="diet-view-comments__text">
                    {dietData?.comments}
                  </span>
                </div>
              )}

              <div className="diet-periods">
                {SECTION_CONFIG.map((section) =>
                  renderSection(section, dietData?.slots?.[section.key])
                )}
              </div>
            </div>
          )}
        </>
      )}
      <DietHistoryDrawer
        open={isActive ? historyVisible : true}
        onClose={() => setHistoryVisible(false)}
      />
    </div>
  );
};

export default DietView;
